import { useCallback, useEffect } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import { IPackage } from '@root/types/package';
import useUnBindDevices from '../useUnBindDevices';
import usePackages from './usePackages';
import useBillAddress from '../useBillAddress';
import useCreateOrder from './useCreateOrder';
import { getQueryString } from '@root/shared';

const useNewSubscription = () => {
  const navigate = useNavigate();
  const navigateType = useNavigationType();
  const { isBillAddress, onAddBill } = useBillAddress();
  const { list: unBindDevices } = useUnBindDevices();
  const {
    packages,
    loading,
    updateTime,
    packageId: statePackageId,
    selectedPackage,
    getPackages,
    setSelectedPackage,
    resetSelectedData,
    setPackages,
  } = usePackages();
  const { paying, onCreateOrder } = useCreateOrder();

  // 套餐升降级
  const changePackageId = getQueryString('changePackageId') || '';
  const isSupportRenew = getQueryString('isSupportRenew') || undefined;
  const planUnit = getQueryString('planUnit') || undefined;
  const excludeLevel = getQueryString('excludeLevel') || undefined;

  const onSelected = (item: IPackage, checkedPriceId: string) => {
    item.checkedPriceId = checkedPriceId;
    setSelectedPackage({ ...item, checkedPriceId });
  };

  const onClickPay = useCallback(() => {
    if (!selectedPackage) {
      return;
    }

    if (!isBillAddress) {
      onAddBill();
      return;
    }
    // 存在设备未绑定 || 套餐升降级
    if (unBindDevices.length || changePackageId) {
      navigate(
        `/packages/${selectedPackage.goodsId}/selectDevice${
          changePackageId ? `?changePackageId=${changePackageId}` : ''
        } `,
      );
      return;
    }

    onCreateOrder({ packageInfo: selectedPackage });
  }, [
    selectedPackage,
    isBillAddress,
    unBindDevices.length,
    onCreateOrder,
    onAddBill,
    navigate,
    changePackageId,
  ]);

  const getPackageList = useCallback(() => {
    const _data = { isSupportRenew, planUnit, excludeLevel };
    getPackages(_data, changePackageId);
  }, [excludeLevel, getPackages, isSupportRenew, changePackageId, planUnit]);

  useEffect(() => {
    // 30s 内无需要重复取数据
    if (
      statePackageId === changePackageId &&
      updateTime &&
      new Date().getTime() - updateTime < 30 * 1000
    ) {
      return;
    }

    getPackageList();
  }, [
    getPackageList,
    changePackageId,
    setPackages,
    statePackageId,
    updateTime,
  ]);

  useEffect(() => {
    if (navigateType === 'PUSH') {
      resetSelectedData();
    }
  }, [navigateType, resetSelectedData]);

  return {
    state: {
      changePackageId,
      unBindDevices,
      selectedPackage,
      packages,
      // statePackageId === packageId 数据静默更新
      loading: updateTime && statePackageId === changePackageId ? 1 : loading,
      paying,
    },
    action: {
      onClickPay,
      setSelectedPackage,
      onSelected,
      getPackageList,
    },
  };
};

export default useNewSubscription;
